import { useState } from "react";
import { LayoutGrid, List, Table2, Layers } from "lucide-react";
import { iniciales } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Empty } from "@/components/Common";

const VISTAS = [
  { v: "tarjetas", label: "Tarjetas", icon: LayoutGrid },
  { v: "lista", label: "Lista", icon: List },
  { v: "tabla", label: "Tabla", icon: Table2 },
];

export const useVistaEstudiantes = (clave = "estudiantes") => {
  const [vista, setVista] = useState(() => localStorage.getItem(`sis_vista_${clave}`) || "tarjetas");
  const [agrupado, setAgrupado] = useState(
    () => localStorage.getItem(`sis_agrupa_${clave}`) === "1");
  const cambiarVista = (v) => { setVista(v); localStorage.setItem(`sis_vista_${clave}`, v); };
  const cambiarAgrupado = (v) => {
    setAgrupado(v);
    localStorage.setItem(`sis_agrupa_${clave}`, v ? "1" : "0");
  };
  return { vista, agrupado, cambiarVista, cambiarAgrupado };
};

export const SelectorVista = ({ vista, agrupado, onVista, onAgrupado,
                                etiquetaAgrupar = "Agrupar por grado",
                                prefijo = "vista", tidAgrupar = "agrupar-grado" }) => (
  <div className="flex flex-wrap items-center gap-2">
    <div className="flex gap-1 rounded-full border border-border bg-card p-1">
      {VISTAS.map((o) => (
        <button key={o.v} onClick={() => onVista(o.v)} title={o.label}
                data-testid={`${prefijo}-${o.v}`}
                className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs lift ${
                  vista === o.v ? "bg-primary font-semibold text-primary-foreground"
                                : "text-muted-foreground hover:bg-muted"}`}>
          <o.icon className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{o.label}</span>
        </button>
      ))}
    </div>
    <button onClick={() => onAgrupado(!agrupado)} data-testid={tidAgrupar}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs lift ${
              agrupado ? "border-primary bg-primary/5 font-semibold" : "border-border text-muted-foreground"}`}>
      <Layers className="h-3.5 w-3.5" /> {etiquetaAgrupar}
    </button>
  </div>
);

const Tarjeta = ({ e, planes, onFicha, acciones }) => (
  <article className="lift rounded-lg border border-border/60 bg-card p-6">
    <button className="w-full text-left" onClick={() => onFicha(e.id)}
            data-testid={`ver-ficha-${e.matricula}`}>
      <div className="flex items-start gap-4">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-primary/10 font-display font-bold text-primary">
          {iniciales(e.nombre)}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-base font-semibold">{e.nombre}</h3>
          <p className="font-mono text-xs text-muted-foreground">{e.matricula}</p>
        </div>
        <Badge variant={e.estado === "activo" ? "secondary" : "outline"}>{e.estado}</Badge>
      </div>
      <dl className="mt-5 space-y-2 text-sm">
        <div className="flex justify-between"><dt className="text-muted-foreground">Grado</dt><dd>{e.grado} {e.seccion}</dd></div>
        <div className="flex justify-between"><dt className="text-muted-foreground">Tutor</dt><dd>{e.tutor_nombre || "—"}</dd></div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Modalidad</dt>
          <dd>{planes.find((p) => p.id === e.plan_pago_id)?.nombre || "sin asignar"}</dd>
        </div>
        {e.diagnostico && (
          <div className="flex justify-between"><dt className="text-muted-foreground">Diagnóstico</dt><dd>{e.diagnostico}</dd></div>
        )}
      </dl>
      <p className="mt-3 text-[10px] uppercase tracking-widest text-primary">Ver ficha completa</p>
    </button>
    <div className="mt-5 flex flex-wrap gap-2">{acciones(e)}</div>
  </article>
);

const Fila = ({ e, onFicha, acciones }) => (
  <div className="flex flex-wrap items-center gap-3 border-b border-border/60 px-4 py-3 last:border-0 hover:bg-muted/30">
    <button className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => onFicha(e.id)}
            data-testid={`ver-ficha-${e.matricula}`}>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-bold text-primary">
        {iniciales(e.nombre)}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{e.nombre}</span>
        <span className="block truncate font-mono text-xs text-muted-foreground">
          {e.matricula} · {e.grado}{e.seccion ? ` ${e.seccion}` : ""} · {e.tutor_nombre || "sin tutor"}
        </span>
      </span>
    </button>
    <div className="flex flex-wrap gap-2">{acciones(e)}</div>
  </div>
);

const Tabla = ({ estudiantes, planes, onFicha, acciones }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[720px] text-sm">
      <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
        <tr>
          <th className="px-4 py-3">Estudiante</th>
          <th className="px-4 py-3">Matrícula</th>
          <th className="px-4 py-3">Grado</th>
          <th className="px-4 py-3">Tutor</th>
          <th className="px-4 py-3">Modalidad</th>
          <th className="px-4 py-3"></th>
        </tr>
      </thead>
      <tbody>
        {estudiantes.map((e) => (
          <tr key={e.id} className="border-t border-border/60 hover:bg-muted/30">
            <td className="px-4 py-3">
              <button className="text-left font-medium hover:text-primary" onClick={() => onFicha(e.id)}
                      data-testid={`ver-ficha-${e.matricula}`}>
                {e.nombre}
              </button>
            </td>
            <td className="px-4 py-3 font-mono text-xs">{e.matricula}</td>
            <td className="px-4 py-3">{e.grado} {e.seccion}</td>
            <td className="px-4 py-3 text-muted-foreground">{e.tutor_nombre || "—"}</td>
            <td className="px-4 py-3 text-muted-foreground">
              {planes.find((p) => p.id === e.plan_pago_id)?.nombre || "sin asignar"}
            </td>
            <td className="px-4 py-3">
              <div className="flex flex-wrap justify-end gap-2">{acciones(e)}</div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Bloque = ({ vista, estudiantes, planes, onFicha, acciones }) => {
  if (vista === "tarjetas") {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {estudiantes.map((e) => (
          <Tarjeta key={e.id} e={e} planes={planes} onFicha={onFicha} acciones={acciones} />
        ))}
      </div>
    );
  }
  if (vista === "lista") {
    return (
      <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
        {estudiantes.map((e) => <Fila key={e.id} e={e} onFicha={onFicha} acciones={acciones} />)}
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
      <Tabla estudiantes={estudiantes} planes={planes} onFicha={onFicha} acciones={acciones} />
    </div>
  );
};

const porGradoSeccion = (estudiantes) => {
  const grupos = new Map();
  estudiantes.forEach((e) => {
    const clave = `${e.grado || "Sin asignar"}${e.seccion ? ` · Sección ${e.seccion}` : ""}`;
    grupos.set(clave, [...(grupos.get(clave) || []), e]);
  });
  return [...grupos.entries()].sort((a, b) => a[0].localeCompare(b[0], "es"));
};

/** Fichas de estudiantes en tarjetas, lista compacta o tabla, con agrupación por grado. */
export const VistaEstudiantes = ({ estudiantes, planes, vista, agrupado, onFicha, acciones, vacio }) => {
  if (estudiantes.length === 0) return <Empty testId="estudiantes-vacios">{vacio}</Empty>;
  if (!agrupado) {
    return (
      <div data-testid="lista-estudiantes">
        <Bloque vista={vista} estudiantes={estudiantes} planes={planes} onFicha={onFicha}
                acciones={acciones} />
      </div>
    );
  }
  return (
    <div className="space-y-8" data-testid="lista-estudiantes">
      {porGradoSeccion(estudiantes).map(([clave, grupo]) => (
        <section key={clave} data-testid={`grupo-${clave}`}>
          <div className="mb-3 flex items-center gap-3">
            <h3 className="font-display text-base font-semibold">{clave}</h3>
            <Badge variant="outline" data-testid={`contador-grupo-${clave}`}>{grupo.length}</Badge>
          </div>
          <Bloque vista={vista} estudiantes={grupo} planes={planes} onFicha={onFicha}
                  acciones={acciones} />
        </section>
      ))}
    </div>
  );
};
